import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Environment validation for deployment
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase environment variables not configured. Auth features will be disabled.')
}

// Create client with fallback for missing env vars
export const supabase = createClient(
  supabaseUrl || 'https://wsvftjxsxtbnpbmhcrmy.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      // Temporary fix for production domain CORS issue
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce'
    }
  }
)

// Types for our beta tracking
export interface BetaUser {
  id: string
  email?: string | null
  name?: string | null
  age?: number | null
  experience_level?: 'anfaenger' | 'fortgeschritten' | 'erfahren' | 'beginner' | 'intermediate' | 'experienced' | null
  training_goal?: string[] | string | null
  created_at: string
  updated_at?: string
  approved?: boolean | null
  admin_notes?: string | null
  approved_at?: string | null
  approved_by?: string | null
  profile_completed?: boolean | null
}

export interface TrainingSession {
  id: string
  user_id: string
  date: string
  duration: number
  content: string
  notes?: string
  rating: number // 1-5 stars
  goal_achievement: 'helped' | 'neutral' | 'not_helped' | null // Hat bei Zielerreichung geholfen?
  created_at: string
}

export interface UserStatusAssessment {
  id: string
  user_id: string
  assessment_date: string
  fitness_level: number // 1-10 scale
  endurance_level: number // 1-10 scale
  technique_level: number // 1-10 scale
  confidence_level: number // 1-10 scale
  motivation_level: number // 1-10 scale
  notes?: string
  created_at: string
}

// Auth helpers
export const getCurrentUser = () => {
  return supabase.auth.getUser()
}

export const signInWithEmail = (email: string, password: string) => {
  return supabase.auth.signInWithPassword({ email, password })
}

export const signUpWithEmail = (email: string, password: string) => {
  return supabase.auth.signUp({ email, password })
}

export const signOut = () => {
  return supabase.auth.signOut()
}

// Database helpers
type BetaUserInsert = {
  id?: string
  email?: string | null
  name?: string | null
  age?: number | null
  experience_level?: BetaUser['experience_level']
  training_goal?: string[] | string | null
  approved?: boolean | null
  admin_notes?: string | null
  approved_at?: string | null
  approved_by?: string | null
  profile_completed?: boolean | null
}

export const createBetaUser = (userData: BetaUserInsert) => {
  return supabase
    .from('beta_users')
    .insert({
      ...userData,
      profile_completed: userData.profile_completed ?? false,
      created_at: new Date().toISOString(),
      approved: userData.approved ?? false  // Standardmäßig nicht freigegeben
    })
    .select()
    .single()
}

export const getBetaUser = (userId: string) => {
  return supabase
    .from('beta_users')
    .select('*')
    .eq('id', userId)
    .single()
}

export const getBetaUserByEmail = (email: string) => {
  return supabase
    .from('beta_users')
    .select('*')
    .eq('email', email)
    .order('created_at', { ascending: false }) // Newest first
    .limit(1)
    .maybeSingle() // Use maybeSingle() to handle case when no user exists
}

export const updateBetaUser = (userId: string, updates: Partial<BetaUser>) => {
  const payload: Record<string, any> = {
    ...updates,
    updated_at: new Date().toISOString()
  }

  if (updates.profile_completed === undefined) {
    delete payload.profile_completed
  }

  return supabase
    .from('beta_users')
    .update(payload)
    .eq('id', userId)
    .select()
    .single()
}

export const createTrainingSession = (sessionData: Omit<TrainingSession, 'id' | 'created_at'>) => {
  return supabase
    .from('training_sessions')
    .insert({
      ...sessionData,
      created_at: new Date().toISOString()
    })
    .select()
    .single()
}

export const createUserStatusAssessment = (assessmentData: Omit<UserStatusAssessment, 'id' | 'created_at'>) => {
  return supabase
    .from('user_status_assessments')
    .insert({
      ...assessmentData,
      created_at: new Date().toISOString()
    })
    .select()
    .single()
}

export const getUserStatusAssessments = (userId: string) => {
  return supabase
    .from('user_status_assessments')
    .select('*')
    .eq('user_id', userId)
    .order('assessment_date', { ascending: false })
}

export const getLatestUserStatusAssessment = (userId: string) => {
  return supabase
    .from('user_status_assessments')
    .select('*')
    .eq('user_id', userId)
    .order('assessment_date', { ascending: false })
    .limit(1)
    .single()
}

export const getTrainingSessions = (userId: string) => {
  return supabase
    .from('training_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
}
